import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import { ACCOUNT_DETAIL_FONT_SX } from "../../components/AccountDetailPolicy/accountDetailTypography";
import {
  clearSsoConfigMessages,
  fetchSsoConfig,
  saveSsoConfig,
} from "../../store/ssoConfig/ssoConfigSlice";

const PROVIDERS = [
  { value: "OKTA", displayName: "Okta" },
  { value: "OneLogin", displayName: "OneLogin" },
  { value: "AD", displayName: "AD", label: "Microsoft Entra ID" },
];

const PREFILL_DISPLAY_NAMES = PROVIDERS.map((provider) => provider.displayName);

const DOMAIN_PATTERN =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const EDITABLE_FIELDS = [
  "displayName",
  "targetEmailDomain",
  "idpMetadataUrl",
  "enabled",
  "configStatus",
];

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "DISABLED", label: "Disabled" },
  { value: "ERROR", label: "Error" },
];

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const FIELD_LABEL_SX = {
  display: "block",
  mb: 0.75,
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "#334155",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    color: "#111827",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
    "&:hover:not(.Mui-disabled) fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-disabled": {
      backgroundColor: "#f3f4f6",
      "& fieldset": { borderColor: "#e5e7eb" },
    },
    "&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiSelect-select": {
      WebkitTextFillColor: "#64748b",
    },
    "&.Mui-disabled .MuiSelect-icon": {
      color: "#94a3b8",
    },
  },
  "& .MuiOutlinedInput-notchedOutline legend": {
    float: "unset",
    width: "auto",
    maxWidth: "0.01px",
    margin: 0,
    padding: 0,
    fontSize: "0.75em",
    lineHeight: "11px",
    height: "11px",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#94a3b8",
    opacity: 1,
  },
};

const INPUT_ERROR_SX = {
  "& .MuiOutlinedInput-root.Mui-error fieldset, & .MuiOutlinedInput-root.Mui-error:hover fieldset, & .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset":
    { borderColor: "#dc2626" },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.75,
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  "& .MuiFormHelperText-root.Mui-error": { color: "#dc2626" },
};

const TOGGLE_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  px: 2,
  py: 1.5,
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  backgroundColor: "#fff",
};

const ENABLED_SWITCH_SX = {
  flexShrink: 0,
  mr: -1,
  "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_ORANGE },
  "& .MuiSwitch-switchBase.Mui-checked:hover": {
    backgroundColor: "rgba(249, 129, 37, 0.08)",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: BRAND_ORANGE,
    opacity: 0.5,
  },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#fff4eb",
      },
    },
  },
};

const TWO_COL_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 2,
  alignItems: "start",
};

const VALUE_BOX_SX = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  backgroundColor: "#f3f4f6",
  px: 1.5,
  py: 1,
  cursor: "default",
};

const REQUIRED_MARK_SX = {
  color: "#dc2626",
  ml: 0.25,
};

const PRIMARY_BTN_SX = {
  height: 40,
  minWidth: 112,
  backgroundColor: BRAND_ORANGE,
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  boxShadow: "none",
  "&:hover": { backgroundColor: BRAND_ORANGE_HOVER, boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const COPY_BTN_SX = {
  height: 40,
  minWidth: 92,
  flexShrink: 0,
  px: 1.75,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  color: "#111827",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#9ca3af",
    borderColor: "#e5e7eb",
    backgroundColor: "#fff",
  },
};

const ALERT_SX = {
  borderRadius: "8px",
  fontSize: "0.8125rem",
  alignItems: "center",
};

const EMPTY_FORM = {
  providerType: "OKTA",
  displayName: "Okta",
  targetEmailDomain: "",
  idpMetadataUrl: "",
  enabled: false,
  configStatus: "DRAFT",
  protocol: "SAML",
  acsUrl: "",
  audienceUri: "",
  cognitoProviderName: "",
};

function readRole() {
  try {
    return getUserRole();
  } catch (error) {
    return "NONE";
  }
}

function isHttpsUrl(value) {
  if (/\s/.test(value)) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" && Boolean(parsed.hostname);
  } catch (error) {
    return false;
  }
}

function comparableValue(form, field) {
  const value = form[field];
  return typeof value === "string" ? value.trim() : value;
}

function isSavedConfig(config) {
  return Boolean(config && String(config.idpMetadataUrl || "").trim());
}

function formFromConfig(config, fallbackProvider) {
  const providerType = config?.providerType || fallbackProvider || "OKTA";
  const provider = PROVIDERS.find((item) => item.value === providerType);
  return {
    providerType,
    displayName: config?.displayName || provider?.displayName || "",
    targetEmailDomain: config?.targetEmailDomain || "",
    idpMetadataUrl: config?.idpMetadataUrl || "",
    enabled: Boolean(config?.enabled),
    configStatus: config?.status || "DRAFT",
    protocol: config?.protocol || "SAML",
    acsUrl: config?.acsUrl || "",
    audienceUri: config?.audienceUri || "",
    cognitoProviderName: config?.cognitoProviderName || "",
  };
}

export default function SsoConfig() {
  const dispatch = useDispatch();
  const { loading, saving, config, error, saveError, saveMessage } = useSelector(
    (state) => state.ssoConfig
  );
  const role = readRole();
  const canWrite = role === "Administrator";
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [touched, setTouched] = useState({});
  const [confirmTurnOffOpen, setConfirmTurnOffOpen] = useState(false);
  const savedForm = isSavedConfig(config) ? formFromConfig(config) : null;
  const lockedProvider = isSavedConfig(config) ? config.providerType || "" : "";
  const lockedProviderLabel =
    PROVIDERS.find((item) => item.value === lockedProvider)?.displayName || lockedProvider;
  const providerLabel =
    PROVIDERS.find((item) => item.value === form.providerType)?.displayName || form.providerType;
  const isStatusActive = form.configStatus === "ACTIVE";
  // Turning on requires Active; turning off stays available so an invalid state can be cleared.
  const enabledSwitchDisabled = !canWrite || (!form.enabled && !isStatusActive);

  useEffect(() => {
    dispatch(fetchSsoConfig({ providerType: "OKTA" }));
    return () => {
      dispatch(clearSsoConfigMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!config) return;
    if (isSavedConfig(config)) {
      setForm(formFromConfig(config));
      setHasExistingConfig(true);
      return;
    }
    setForm((current) => {
      const next = formFromConfig(config, current.providerType);
      const keepDisplayName =
        current.displayName &&
        !PREFILL_DISPLAY_NAMES.includes(current.displayName);
      return {
        ...current,
        providerType: next.providerType || current.providerType,
        displayName: keepDisplayName ? current.displayName : next.displayName,
        protocol: next.protocol || current.protocol,
        acsUrl: next.acsUrl,
        audienceUri: next.audienceUri,
        cognitoProviderName: next.cognitoProviderName,
      };
    });
  }, [config]);

  useEffect(() => {
    if (saveError?.status === 409) setHasExistingConfig(true);
    if (saveError?.status === 404) setHasExistingConfig(false);
  }, [saveError]);

  useEffect(() => {
    if (!saveMessage) return;
    enqueueSnackbar(saveMessage, { variant: "success" });
    dispatch(clearSsoConfigMessages());
  }, [saveMessage, dispatch]);

  useEffect(() => {
    if (!copiedField) return undefined;
    const timer = setTimeout(() => setCopiedField(""), 2000);
    return () => clearTimeout(timer);
  }, [copiedField]);

  const setField = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const markTouched = (field) => () => {
    setTouched((current) => (current[field] ? current : { ...current, [field]: true }));
  };

  const handleProviderChange = (event) => {
    if (lockedProvider) return;
    const providerType = event.target.value;
    const provider = PROVIDERS.find((item) => item.value === providerType);
    setForm((current) => ({
      ...current,
      providerType,
      displayName: PREFILL_DISPLAY_NAMES.includes(current.displayName)
        ? provider.displayName
        : current.displayName,
    }));
    setCopiedField("");
    dispatch(clearSsoConfigMessages());
    dispatch(fetchSsoConfig({ providerType }));
  };

  const copyValue = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
    } catch (copyError) {
      setCopiedField("");
    }
  };

  const submitSave = () => {
    setConfirmTurnOffOpen(false);
    dispatch(clearSsoConfigMessages());
    dispatch(
      saveSsoConfig({
        method: hasExistingConfig ? "PUT" : "POST",
        body: {
          enabled: form.enabled,
          providerType: form.providerType,
          displayName: form.displayName.trim(),
          protocol: "SAML",
          targetEmailDomain: form.targetEmailDomain.trim(),
          idpMetadataUrl: form.idpMetadataUrl.trim(),
          status: form.configStatus,
        },
      })
    );
  };

  const isLiveSaved = Boolean(savedForm?.enabled && savedForm.configStatus === "ACTIVE");
  const willBeLive = form.enabled && isStatusActive;

  const handleSave = () => {
    if (isLiveSaved && !willBeLive) {
      setConfirmTurnOffOpen(true);
      return;
    }
    submitSave();
  };

  const isDirty =
    !savedForm ||
    EDITABLE_FIELDS.some(
      (field) => comparableValue(form, field) !== comparableValue(savedForm, field)
    );

  const displayNameError = /\s/.test(form.displayName)
    ? "Display name cannot contain spaces."
    : "";

  const emailDomain = form.targetEmailDomain.trim();
  const emailDomainError =
    emailDomain && !DOMAIN_PATTERN.test(emailDomain)
      ? "Enter a domain like company.com, without @ or spaces."
      : "";

  const metadataUrl = form.idpMetadataUrl.trim();
  const metadataUrlError =
    metadataUrl && !isHttpsUrl(metadataUrl)
      ? "Enter a valid URL that starts with https://."
      : "";

  const saveBlockedReason = () => {
    if (!form.displayName.trim()) return "Display name is required.";
    if (displayNameError) return displayNameError;
    if (!emailDomain) return "Email domain is required.";
    if (emailDomainError) return emailDomainError;
    if (!metadataUrl) return "SAML metadata URL is required.";
    if (metadataUrlError) return metadataUrlError;
    if (form.enabled && form.configStatus !== "ACTIVE") {
      return "Single Sign-On can only be enabled when status is Active.";
    }
    return "";
  };

  const blockedReason = saveBlockedReason();

  const showAlerts = Boolean(error || saveError?.message || !canWrite);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}>
          Single Sign-On
        </Typography>
      </Box>

      <Typography
        sx={{
          color: "#64748b",
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: 1.5,
          mb: 2,
        }}
      >
        Copy the CloudCatcher values into the identity provider, then save the provider metadata URL.
      </Typography>

      {(loading || saving) && (
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: "4px",
            backgroundColor: "#ffedd5",
            "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
          }}
        />
      )}

      {showAlerts && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
          {error && <Alert severity="error" sx={ALERT_SX}>{error}</Alert>}
          {saveError?.message && (
            <Alert severity="error" sx={ALERT_SX}>{saveError.message}</Alert>
          )}
          {!canWrite && (
            <Alert severity="info" sx={ALERT_SX}>
              Only an Administrator can save Single Sign-On configuration.
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionCard title="CloudCatcher Values">
          <CopyField
            label="ACS URL"
            value={form.acsUrl}
            copied={copiedField === "acsUrl"}
            onCopy={() => copyValue("acsUrl", form.acsUrl)}
          />
          <CopyField
            label="Audience URI / SP Entity ID"
            value={form.audienceUri}
            copied={copiedField === "audienceUri"}
            onCopy={() => copyValue("audienceUri", form.audienceUri)}
          />
          <Box sx={TWO_COL_SX}>
            <ReadOnlyValue label="Cognito provider name" value={form.cognitoProviderName} />
            <ReadOnlyValue label="Protocol" value={form.protocol} />
          </Box>
        </SectionCard>

        <SectionCard title="Identity Provider">
          <Box sx={TWO_COL_SX}>
            <Field label="Provider" htmlFor="sso-provider">
              <Tooltip
                title={
                  lockedProvider
                    ? `This organization already uses ${lockedProviderLabel}. Only one identity provider can be configured.`
                    : ""
                }
                placement="top"
                arrow
              >
                <Box component="span" sx={{ display: "block" }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    id="sso-provider"
                    value={form.providerType}
                    onChange={handleProviderChange}
                    disabled={loading || Boolean(lockedProvider)}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    sx={INPUT_SX}
                  >
                    {PROVIDERS.map((provider) => (
                      <MenuItem key={provider.value} value={provider.value}>
                        {provider.label || provider.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Tooltip>
            </Field>
            <Field label="Display name" htmlFor="sso-display-name" required>
              <TextField
                fullWidth
                size="small"
                id="sso-display-name"
                value={form.displayName}
                onChange={setField("displayName")}
                disabled={!canWrite}
                required
                error={Boolean(displayNameError)}
                helperText={displayNameError || undefined}
                sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
              />
            </Field>
            <Field label="Email domain" htmlFor="sso-email-domain" required>
              <TextField
                fullWidth
                size="small"
                id="sso-email-domain"
                value={form.targetEmailDomain}
                onChange={setField("targetEmailDomain")}
                onBlur={markTouched("targetEmailDomain")}
                placeholder="customer.com"
                disabled={!canWrite}
                required
                error={Boolean(touched.targetEmailDomain && emailDomainError)}
                helperText={(touched.targetEmailDomain && emailDomainError) || undefined}
                sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
              />
            </Field>
            <Field label="Status" htmlFor="sso-status">
              <TextField
                select
                fullWidth
                size="small"
                id="sso-status"
                value={form.configStatus}
                onChange={setField("configStatus")}
                disabled={!canWrite}
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                sx={INPUT_SX}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          </Box>

          <Field label="SAML metadata URL" htmlFor="sso-metadata-url" required>
            <TextField
              fullWidth
              size="small"
              id="sso-metadata-url"
              value={form.idpMetadataUrl}
              onChange={setField("idpMetadataUrl")}
              onBlur={markTouched("idpMetadataUrl")}
              disabled={!canWrite}
              required
              error={Boolean(touched.idpMetadataUrl && metadataUrlError)}
              helperText={(touched.idpMetadataUrl && metadataUrlError) || undefined}
              sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
              inputProps={{
                spellCheck: "false",
                title: form.idpMetadataUrl || undefined,
              }}
            />
          </Field>

          <Box sx={TOGGLE_ROW_SX}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="label"
                htmlFor="sso-enabled"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: enabledSwitchDisabled ? "#94a3b8" : "#111827",
                  cursor: enabledSwitchDisabled ? "default" : "pointer",
                }}
              >
                Enable Single Sign-On
              </Typography>
              <Typography
                id="sso-enabled-description"
                sx={{ fontSize: "0.8125rem", lineHeight: 1.5, color: "#64748b", mt: 0.25 }}
              >
                Users set to Single Sign-On sign in through {providerLabel} instead of a password.
                {!form.enabled && !isStatusActive && " Set Status to Active to turn this on."}
              </Typography>
            </Box>
            <Switch
              id="sso-enabled"
              checked={form.enabled}
              onChange={setField("enabled")}
              disabled={enabledSwitchDisabled}
              inputProps={{ "aria-describedby": "sso-enabled-description" }}
              sx={ENABLED_SWITCH_SX}
            />
          </Box>

          {canWrite && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Button
                onClick={handleSave}
                disabled={saving || loading || Boolean(blockedReason) || !isDirty}
                sx={PRIMARY_BTN_SX}
              >
                {saving ? "Saving..." : hasExistingConfig ? "Update" : "Create"}
              </Button>
              {blockedReason && (
                <Typography
                  sx={{ color: "#64748b", fontSize: "0.8125rem", mt: 1, textAlign: "right" }}
                >
                  {blockedReason}
                </Typography>
              )}
            </Box>
          )}
        </SectionCard>
      </Box>

      <ConfirmTurnOffDialog
        open={confirmTurnOffOpen}
        onCancel={() => setConfirmTurnOffOpen(false)}
        onConfirm={submitSave}
      />
    </Box>
  );
}

function ConfirmTurnOffDialog({ open, onCancel, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: { xs: "calc(100% - 24px)", sm: "100%" },
          m: { xs: 1.5, sm: 2 },
          overflow: "hidden",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "#FFFFFF",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Turn Off Single Sign-On
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onCancel}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Typography
          sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.5, mb: 1 }}
        >
          Are you sure you want to turn off Single Sign-On for this organization?
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.5 }}>
          Users set to Single Sign-On will no longer be offered Single Sign-On when they sign
          in. Anyone already signed in may stay signed in until they sign out or their session
          expires.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1.25,
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: "8px",
              px: 2.5,
              color: "#475569",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={onConfirm}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
              backgroundColor: BRAND_ORANGE,
              "&:hover": { backgroundColor: BRAND_ORANGE_HOVER, boxShadow: "none" },
            }}
          >
            Turn Off
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function SectionCard({ title, children }) {
  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        backgroundColor: "#fff",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.875rem" }}>
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Field({ label, htmlFor, required = false, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="label" htmlFor={htmlFor} sx={FIELD_LABEL_SX}>
        {label}
        {required && (
          <Box component="span" aria-hidden="true" sx={REQUIRED_MARK_SX}>
            *
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  );
}

function ReadOnlyValue({ label, value }) {
  const hasValue = Boolean(value);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={VALUE_BOX_SX}>
        <Typography
          component="div"
          sx={{
            width: "100%",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: hasValue ? "#64748b" : "#94a3b8",
            wordBreak: "break-all",
          }}
        >
          {hasValue ? value : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function CopyField({ label, value, copied, onCopy }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
        <Box sx={{ ...VALUE_BOX_SX, flex: 1 }}>
          <Typography
            component="div"
            sx={{
              width: "100%",
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              color: value ? "#64748b" : "#94a3b8",
              wordBreak: "break-all",
              userSelect: "all",
            }}
          >
            {value || "—"}
          </Typography>
        </Box>
        <Button
          onClick={onCopy}
          disabled={!value}
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
          sx={{
            ...COPY_BTN_SX,
            ...(copied
              ? {
                  color: BRAND_ORANGE,
                  borderColor: "#fdba74",
                  backgroundColor: "#fff7ed",
                  "&:hover": {
                    backgroundColor: "#fff7ed",
                    borderColor: "#fdba74",
                    boxShadow: "none",
                  },
                }
              : {}),
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </Box>
    </Box>
  );
}
